import { useEffect } from 'react';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { Skeleton, SectionErrorBoundary } from '@shared/ui';
import { SignedImg } from '@shared/ui/SignedMedia';
import { currentLocale } from '@shared/i18n';

/** One row from public.get_um_brand_tag_posts. */
interface PostRow {
  post_url: string | null;
  caption: string | null;
  thumb_url: string | null;
  posted_at: string | null;
  brands: string[] | null;
  via: string[] | null;
}

interface Props {
  handle: string;
  onClose: () => void;
}

export default function BrandTagPostsModal({ handle, onClose }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const q = useSupabaseQuery<PostRow[]>(
    ['um-brand-tag-posts', handle],
    async () => supabase.rpc('get_um_brand_tag_posts', { p_handle: handle }),
  );
  const rows = q.data ?? [];

  return (
    <div
      role="dialog" aria-modal="true" aria-label={`Brand-tagged posts: ${handle}`}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 100,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '4vh 16px', overflowY: 'auto', backdropFilter: 'blur(6px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 1100, background: 'var(--bg-elev, #161b22)',
          border: '1px solid var(--border-strong, #2a2d35)', borderRadius: 14, padding: 24,
          boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
        }}
      >
        <SectionErrorBoundary name="brand-tag-posts">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 'var(--fs-caption)', textTransform: 'uppercase', letterSpacing: 0.5, color: '#9aa' }}>
                Recent posts tagging known brands
              </div>
              <h2 style={{ margin: '4px 0 0', fontSize: 'var(--fs-h1)', fontWeight: 'var(--fw-bold)' }}>@{handle}</h2>
              <div style={{ marginTop: 6, fontSize: 'var(--fs-body)', color: '#9aa' }}>
                {q.isLoading ? 'Loading…' : `${rows.length} post${rows.length === 1 ? '' : 's'} · @mentions and photo tags`}
              </div>
            </div>
            <button
              type="button" onClick={onClose} aria-label="Close"
              style={{
                background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: '#dde',
                width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 'var(--fs-label)', lineHeight: 1, fontFamily: 'inherit',
              }}
            >×</button>
          </div>

          {q.isLoading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={240} />)}
            </div>
          ) : q.error ? (
            <div style={{ color: 'var(--danger)' }}>{String((q.error as Error)?.message ?? q.error)}</div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 24, color: '#9aa', textAlign: 'center', background: 'rgba(15,45,100,0.05)', border: '1px dashed rgba(15,45,100,0.3)', borderRadius: 10 }}>
              No brand-tagged posts found in the captured recent posts.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
              {rows.map((r, i) => (
                <a
                  key={(r.post_url ?? '') + i}
                  href={r.post_url ?? undefined} target="_blank" rel="noopener noreferrer"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div style={{ background: 'rgba(15,45,100,0.10)', border: '1px solid rgba(15,45,100,0.4)', borderRadius: 10, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <div style={{ position: 'relative', aspectRatio: '1 / 1', background: 'rgba(0,0,0,0.4)' }}>
                      {r.thumb_url ? (
                        <SignedImg src={r.thumb_url} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#556', fontSize: 'var(--fs-h1)' }}>◯</div>
                      )}
                      {(r.via ?? []).length > 0 && (
                        <div style={{ position: 'absolute', top: 6, left: 6, display: 'flex', gap: 4 }}>
                          {(r.via ?? []).map((v) => {
                            const c = v === 'tag' ? '#4a9eff' : v === 'bio' ? '#9B5DE5' : '#2ecc71';
                            return (
                              <span key={v} style={{ fontSize: 'var(--fs-micro)', fontWeight: 'var(--fw-bold)', padding: '2px 6px', borderRadius: 4, background: 'rgba(0,0,0,0.6)', color: c, border: `1px solid ${c}`, letterSpacing: 0.5 }}>
                                {v === 'tag' ? 'TAGGED' : v === 'bio' ? 'BIO' : 'MENTION'}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {(r.brands ?? []).map((b) => (
                          <span key={b} style={{ fontSize: 'var(--fs-micro)', fontWeight: 'var(--fw-semibold)', padding: '2px 6px', borderRadius: 4, background: 'rgba(74,158,255,0.12)', color: '#bcd0ff', border: '1px solid rgba(74,158,255,0.4)' }}>🏷 {b}</span>
                        ))}
                      </div>
                      {r.caption && (
                        <div style={{ fontSize: 'var(--fs-small)', color: '#bbc', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {r.caption}
                        </div>
                      )}
                      <div style={{ marginTop: 'auto', fontSize: 'var(--fs-caption)', color: '#778', paddingTop: 4 }}>
                        {r.posted_at ? new Date(r.posted_at).toLocaleDateString(currentLocale()) : ''}
                      </div>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </SectionErrorBoundary>
      </div>
    </div>
  );
}
